import { FC, useState } from "react";
import {
  StaticListItem,
  StaticListWidgetConfig,
} from "@/models/dashboard/WidgetConfig";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  Skeleton,
} from "@capacit-com/lara-law-shared-ui";
import { useStaticListItemResolver } from "@/hooks/useStaticListItemResolver";
import { useOpenAppNavigation } from "@/hooks/useOpenAppNavigation";
import {
  getStaticListCellValue,
  getStaticListColumns,
  parseLegislationOriginalId,
  resolveStaticListItemType,
  sortStaticListItems,
} from "../lib/staticListItems";
import { resolveVisibleWidgetColumns } from "../lib/widgetColumns";
import {
  legislationTarget,
  NEW_TAB_MODIFIER_TOOLTIP,
  workspaceDocumentTarget,
} from "@/lib/navigation/appNavigation";
import SourceFavoriteCell, {
  SourceFavoriteHead,
} from "../components/SourceFavoriteCell";
import { useFavoriteSourceIds } from "../hooks/useFavoriteSourceIds";
import {
  isSourceFavorited,
  resolveStaticListSourceTarget,
  staticListShowsFavoriteColumn,
} from "../lib/favoriteSourceRows";

interface StaticListWidgetProps {
  widgetId: string;
  name: string;
  config: StaticListWidgetConfig;
}

/** Columns that read straight off the item and never wait on a lookup. */
const isResolvedColumn = (columnId: string) => columnId !== "origin";

const StaticListWidget: FC<StaticListWidgetProps> = ({
  widgetId: _widgetId,
  name: _name,
  config,
}) => {
  const { openNavigation } = useOpenAppNavigation();
  const [hoveredItemId, setHoveredItemId] = useState<string | null>(null);
  const favoriteSourceIds = useFavoriteSourceIds();
  const { getResolvedInfo, isLoading } = useStaticListItemResolver(
    config.items,
    config.dataSource
  );

  const visibleColumns = resolveVisibleWidgetColumns(
    getStaticListColumns(config.dataSource),
    config.visibleColumns
  );

  const sortedItems = sortStaticListItems(config.items);
  const showFavoriteColumn = staticListShowsFavoriteColumn(
    sortedItems,
    config.dataSource
  );

  const itemTypeOf = (item: StaticListItem) =>
    resolveStaticListItemType(item, config.dataSource);

  /** A row is only clickable when we have everything its target needs. */
  const getNavigationTarget = (item: StaticListItem) => {
    if (itemTypeOf(item) === "source") {
      const parsed = parseLegislationOriginalId(item.originalId);
      return parsed
        ? legislationTarget(parsed.externalId, parsed.datasource)
        : null;
    }

    const workspaceId = getResolvedInfo(item.id).workspaceId || item.workspaceId;
    return workspaceId
      ? workspaceDocumentTarget(workspaceId, item.originalId)
      : null;
  };

  if (sortedItems.length === 0) {
    return (
      <div className="text-sm text-muted-foreground text-center py-4">
        Ingen dokumenter tilføjet endnu.
        <br />
        <span className="text-xs">Klik på blyanten for at tilføje dokumenter.</span>
      </div>
    );
  }

  const isTitleColumn = (colId: string) =>
    colId === "title" || colId === "name";

  return (
    <div className="themed-scrollbar h-full overflow-x-auto overflow-y-auto">
      <Table removeOverflowX={true}>
        <TableHeader className="sticky top-0 z-10 shadow-sm">
          <TableRow>
            {visibleColumns.map((col) => (
              <TableHead
                key={col.id}
                className={isTitleColumn(col.id) ? "w-full" : undefined}
              >
                {col.label}
              </TableHead>
            ))}
            {showFavoriteColumn && <SourceFavoriteHead />}
          </TableRow>
        </TableHeader>
        <TableBody>
          {sortedItems.map((item) => {
            const resolved = getResolvedInfo(item.id);
            const target = getNavigationTarget(item);
            const favoriteTarget = resolveStaticListSourceTarget(
              item,
              itemTypeOf(item),
              resolved
            );

            return (
              <TableRow
                key={item.id}
                className={target ? "cursor-pointer hover:bg-muted/50" : ""}
                title={target ? NEW_TAB_MODIFIER_TOOLTIP : undefined}
                onClick={(e) => target && openNavigation(target, e)}
                onMouseEnter={() => setHoveredItemId(item.id)}
                onMouseLeave={() =>
                  setHoveredItemId((current) =>
                    current === item.id ? null : current
                  )
                }
              >
                {visibleColumns.map((col) => {
                  const value = getStaticListCellValue(
                    item,
                    col.id,
                    itemTypeOf(item),
                    resolved
                  );
                  const showTitleTooltip = isTitleColumn(col.id) && value && value !== "-";

                  return (
                    <TableCell
                      key={col.id}
                      className={
                        isTitleColumn(col.id)
                          ? "py-2 w-full max-w-0 min-w-[100px] overflow-hidden"
                          : "py-2"
                      }
                    >
                      {resolved.isLoading && isResolvedColumn(col.id) ? (
                        <Skeleton className="h-4 w-24" />
                      ) : showTitleTooltip ? (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span className="block truncate">
                              {value}
                            </span>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p className="max-w-[300px] break-words">
                              {value}
                            </p>
                          </TooltipContent>
                        </Tooltip>
                      ) : (
                        value
                      )}
                    </TableCell>
                  );
                })}
                {showFavoriteColumn && (
                  <SourceFavoriteCell
                    target={favoriteTarget}
                    isFavorite={
                      favoriteTarget !== null &&
                      isSourceFavorited(
                        favoriteSourceIds,
                        favoriteTarget.externalId
                      )
                    }
                    isHovering={hoveredItemId === item.id}
                  />
                )}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
      {isLoading && (
        <div className="text-xs text-muted-foreground text-center py-2">
          Indlæser dokumentdata...
        </div>
      )}
    </div>
  );
};

export default StaticListWidget;
